/**
 * "Sent to Other" routing modal.
 *
 * Opened when staff click the "Send to Other" button on a call card. Forces the operator
 * to pick from a fixed list of routing reasons (Marketing / House Keeping / Doctor /
 * Miscellaneous) before the PATCH /call-logs/{id}/route call goes out. Optional free-text
 * context (e.g. "Asked for facility manager") is stored on the row's RoutedReason field
 * for later review.
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';

// Enum values must match Healing.Domain.Enums.CallRoutedTo on the server (string-serialised).
export const ROUTED_TO_OPTIONS = [
  { value: 'Marketing',     label: 'Marketing' },
  { value: 'HouseKeeping',  label: 'House Keeping' },
  { value: 'Doctor',        label: 'Doctor' },
  { value: 'Miscellaneous', label: 'Miscellaneous' },
];

// Map for badge rendering elsewhere in the UI (label per enum value).
export const ROUTED_TO_LABELS = ROUTED_TO_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

export default function RouteCallModal({ callLog, onClose, onSubmit }) {
  const [routedTo, setRoutedTo] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = !!routedTo && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try { await onSubmit({ routedTo, reason: reason.trim() || null }); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <div className="modal-header">
          <h2>Send to Other</h2>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="td-muted" style={{ fontSize: '0.82rem', marginBottom: 12 }}>
            Route the call from <strong>{callLog?.callerName || callLog?.phoneNumberRaw}</strong> to a different department.
          </div>

          <div className="form-group">
            <label className="form-label">Route to <span style={{ color: 'var(--red-700)' }}>*</span></label>
            <select
              className="form-control"
              value={routedTo}
              onChange={(e) => setRoutedTo(e.target.value)}
              required
              autoFocus
            >
              <option value="">— Select destination —</option>
              {ROUTED_TO_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Reason / notes (optional)</label>
            <textarea
              className="form-control"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Asked for facility manager about a leaking pipe"
            />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
            {submitting ? 'Routing…' : 'Send'}
          </button>
        </div>
      </form>
    </div>
  );
}
